import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Heading } from '@americanexpress/dls-react';
import { useRoomsContext } from '../../contexts/roomsContext';
import RemoveRoomButton from '../RemoveRoomButton/RemoveRoomButton';
import ChildrenAges from '../ChildrenAges/ChildrenAges';
import bindRoomIndexToUpdateTravelersUtil from '../../utils/bindRoomIndexToUpdateTravelersUtil';
import StepperRow from '../StepperRow/StepperRow';
import Infants from '../ChildrenAges/Infants';
import ChildAgeNote from '../ChildrenAges/ChildAgeNote';
import IncludePets from '../IncludePets/IncludePets';
import {
  DEFAULT_ADULT_TRAVELING,
  DEFAULT_CHILDREN_TRAVELING,
  DEFAULT_INFANTS_TRAVELING,
  MIN_ADULT_TRAVELING,
  MIN_CHILDREN_TRAVELING,
  MIN_INFANTS_TRAVELING,
} from '../../constants/travelersConstants';
import { useConfigContext } from '../../contexts/configContext';

const Room = memo(({ isMultiRoom, canRemove, roomIndex }) => {
  const {
    rooms,
    updateAdults,
    updateChildCount,
    updateInfantCount,
    updatePets,
  } = useRoomsContext();

  const {
    maxAdults,
    maxChildren,
    maxInfants,
    maxCombinedChildrenAndInfants,
    maxTravelersCap,
    hasInfants,
    hasPets,
    hasChildrenAges,
    hasRooms,
  } = useConfigContext();

  const { formatMessage } = useIntl();

  const {
    adults, children, infants, includePets,
  } = rooms[roomIndex];

  const roomId = roomIndex + 1;

  const groupHeadingLabel = formatMessage({ id: 'roomNumber' }, { roomId });
  const globalisedAdultAria = formatMessage({ id: 'adult' });
  const globalisedChildAria = formatMessage({ id: 'child' });
  const globalisedInfantAria = formatMessage({ id: 'infant' });

  const maxAdultsForStepper = maxTravelersCap
    ? maxTravelersCap - children.length - infants.length
    : maxAdults;

  const maxChildrenForStepper = useMemo(() => {
    const valueForChildrenUsingMaxTravelerCap = maxTravelersCap
      && maxTravelersCap - adults - infants.length;

    const valueForChildrenUsingMaxCombinedChildrenAndInfants = maxCombinedChildrenAndInfants
      && maxCombinedChildrenAndInfants - infants.length;

    if (valueForChildrenUsingMaxCombinedChildrenAndInfants !== undefined) {
      return valueForChildrenUsingMaxCombinedChildrenAndInfants;
    }

    if (valueForChildrenUsingMaxTravelerCap !== undefined) {
      return valueForChildrenUsingMaxTravelerCap;
    }

    return maxChildren;
  }, [
    adults,
    infants,
    maxChildren,
    maxTravelersCap,
    maxCombinedChildrenAndInfants,
  ]);

  const maxInfantsForStepper = useMemo(() => {
    const valueForInfantsUsingMaxTravelerCap = maxTravelersCap
      && maxTravelersCap - adults - children.length;

    const valueForInfantsUsingMaxCombinedChildrenAndInfants = maxCombinedChildrenAndInfants
      && maxCombinedChildrenAndInfants - children.length;

    if (valueForInfantsUsingMaxCombinedChildrenAndInfants !== undefined) {
      return valueForInfantsUsingMaxCombinedChildrenAndInfants;
    }
    if (valueForInfantsUsingMaxTravelerCap !== undefined) {
      return valueForInfantsUsingMaxTravelerCap;
    }
    return maxInfants;
  }, [
    adults,
    children,
    maxInfants,
    maxTravelersCap,
    maxCombinedChildrenAndInfants,
  ]);

  const childAgeRangeLabel = useMemo(() => (hasInfants
    ? 'child_age_range'
    : 'child_age_range_including_infants'), [hasInfants]);
  return (
    <div role="group" id="rooms-traveler-modal" className="margin-tb">
      {roomIndex > 0 && (
        <hr className="margin-b" data-testid="room-line-break" />
      )}
      {isMultiRoom && (
        <div className="margin-b flex flex-justify-between flex-align-center">
          <Heading level={2} variant="sans-xsmall-bold">
            {groupHeadingLabel}
          </Heading>
          {canRemove && (
            <RemoveRoomButton classNames="margin-l" roomIndex={roomIndex} />
          )}
        </div>
      )}
      <StepperRow
        className="margin-tb margin-1-t"
        rowLabelId="adult"
        globalisedAria={globalisedAdultAria}
        defaultValue={DEFAULT_ADULT_TRAVELING}
        isMultiRoom={isMultiRoom}
        min={MIN_ADULT_TRAVELING}
        max={maxAdultsForStepper}
        value={adults}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateAdults)}
        roomIndex={roomIndex}
      />
      <hr className="margin-tb" />
      <StepperRow
        className="margin-tb"
        rowLabelId="child"
        globalisedAria={globalisedChildAria}
        rowSubLabelId={childAgeRangeLabel}
        defaultValue={DEFAULT_CHILDREN_TRAVELING}
        isMultiRoom={isMultiRoom}
        min={MIN_CHILDREN_TRAVELING}
        max={maxChildrenForStepper}
        value={children.length}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateChildCount)}
        roomIndex={roomIndex}
      />
      {!hasChildrenAges && children.length > 0 && (
        <ChildrenAges isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
      )}
      {hasInfants && (
        <>
          <hr className="margin-tb" />
          <StepperRow
            className="margin-tb"
            rowLabelId="infant"
            globalisedAria={globalisedInfantAria}
            rowSubLabelId="age_under_2"
            defaultValue={DEFAULT_INFANTS_TRAVELING}
            isMultiRoom={isMultiRoom}
            min={MIN_INFANTS_TRAVELING}
            max={maxInfantsForStepper}
            value={infants.length}
            onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateInfantCount)}
            roomIndex={roomIndex}
          />
          {infants.length > 0 && (
            <ChildAgeNote infants={infants}>
              <Infants isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
            </ChildAgeNote>
          )}
        </>
      )}
      {!isMultiRoom && hasRooms && (
        <hr className="margin-2-tb" />
      )}
      {hasPets && (
        <>
          <hr className="margin-2-tb" />
          <IncludePets
            roomIndex={roomIndex}
            value={includePets}
            onPetsChange={updatePets}
          />
        </>
      )}
    </div>
  );
});

Room.displayName = 'Room';

Room.propTypes = {
  canRemove: PropTypes.bool,
  isMultiRoom: PropTypes.bool,
  roomIndex: PropTypes.number.isRequired,
};

Room.defaultProps = {
  canRemove: false,
  isMultiRoom: false,
};

export default Room;
